/* ──────────────────────────────────────────────────────────────
   Items/index.tsx · Items list screen
   React Native + TypeScript — mirrors view_items.jsx exactly
   ────────────────────────────────────────────────────────────── */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useReloadOnFocus } from '../navigation/useReloadOnFocus';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  ViewStyle,
  TextStyle,
} from 'react-native';

import { useTheme } from '../context/themeContext';
import { useResponsive, CONTENT } from '../lib/responsive';
import { useApp } from '../context/appContext';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { AppStackParamList } from '../navigation/types';
import { SK, Item, ItemType } from '../data/mockData';
import {
  Icon,
  Button,
  Chip,
  Input,
  StateBadge,
  Thumb,
  Popover,
  MenuItem,
  MenuSeparator,
  Empty,
  ConfirmDialog,
  SuccessDialog,
  CommonSearch,
  AppDropdown,
  useToast,
} from '../components/ui';
import { getItems, deleteItem, publishItems } from '../services/catalog/item';
// SKCATALOG-CC-002 CR-1/CR-7 — name a publish refusal's cause; only offer legal
// transitions (SKCATALOG-DB-001 §4).
import { HSN_SAC_REQUIRED, ILLEGAL_TRANSITION } from '../utils/errorHandler';
import { canTransition } from '../utils/lifecycle';
import { getItemTypes } from '../services/catalog/itemType';
import { searchItems } from '../services/catalog/search';
import { handleApiError } from '../utils/errorHandler';
import { useQuota } from '../context/quotaContext';
import { METRIC_CATALOG_ITEMS } from '../schemas/catalog/meter';
import type { CatalogItem } from '../schemas/catalog/item';
import type { ItemType as ApiItemType } from '../schemas/catalog/itemType';

// ── Helpers ──────────────────────────────────────────────────

function fmtINR(n: number): string {
  return '₹' + Number(n).toLocaleString('en-IN');
}

// ── FilterPill ───────────────────────────────────────────────

interface FilterPillProps {
  label: string;
  value: string;
  options: string[];
  onChange: (val: string) => void;
  icon?: string;
}

function FilterPill({ label, value, options, onChange, icon }: FilterPillProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  const isActive = value && value !== 'All';

  const pillStyle: ViewStyle = {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 30,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: isActive ? colors.accentLine : colors.line,
    borderRadius: radii.r2,
    backgroundColor: isActive ? colors.accentSoft : colors.card,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap',
      } as ViewStyle,
      default: {},
    }),
  };

  const textStyle: TextStyle = {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    color: isActive ? colors.accentInk : colors.ink2,
    fontFamily: theme.font.sans,
    includeFontPadding: false,
  };

  const valStyle: TextStyle = {
    ...textStyle,
    fontWeight: fontWeight.semibold,
    color: colors.ink,
  };

  const trigger = (
    <TouchableOpacity style={pillStyle} activeOpacity={0.75}>
      {icon && <Icon name={icon} size={13} color={isActive ? colors.accentInk : colors.ink2} />}
      <Text style={textStyle}>
        {label}
        {isActive && (
          <Text style={textStyle}>
            {': '}
            <Text style={valStyle}>{value}</Text>
          </Text>
        )}
      </Text>
      <Icon name="chev-d" size={12} color={isActive ? colors.accentInk : colors.ink2} />
    </TouchableOpacity>
  );

  return (
    <Popover align="left" width={200} trigger={trigger}>
      {(close: () => void) =>
        options.map(o => (
          <MenuItem key={o} checked={o === value} onPress={() => { onChange(o); close(); }}>
            {o}
          </MenuItem>
        ))
      }
    </Popover>
  );
}

// ── TogglePill ───────────────────────────────────────────────

interface TogglePillProps {
  label: string;
  active: boolean;
  onToggle: () => void;
  icon?: string;
}

function TogglePill({ label, active, onToggle, icon }: TogglePillProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;

  const pillStyle: ViewStyle = {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 30,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: active ? colors.accentLine : colors.line,
    borderRadius: radii.r2,
    backgroundColor: active ? colors.accentSoft : colors.card,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap',
      } as ViewStyle,
      default: {},
    }),
  };

  const textStyle: TextStyle = {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    color: active ? colors.accentInk : colors.ink2,
    fontFamily: theme.font.sans,
    includeFontPadding: false,
  };

  return (
    <TouchableOpacity style={pillStyle} onPress={onToggle} activeOpacity={0.75}>
      {icon && <Icon name={icon} size={13} color={active ? colors.accentInk : colors.ink2} />}
      <Text style={textStyle}>{label}</Text>
    </TouchableOpacity>
  );
}

// ── TableHeader ──────────────────────────────────────────────

interface TableHeaderProps {
  allChecked: boolean;
  onToggleAll: () => void;
}

function TableHeader({ allChecked, onToggleAll }: TableHeaderProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;

  const thStyle: TextStyle = {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.medium,
    color: colors.ink3,
    fontFamily: theme.font.sans,
    textTransform: 'uppercase',
    letterSpacing: 0.04 * fontSize.xs,
    includeFontPadding: false,
  };

  const headerRow: ViewStyle = {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.line,
    backgroundColor: colors.paper2,
  };

  return (
    <View style={headerRow}>
      {/* Checkbox col */}
      <View style={{ width: 36, alignItems: 'center' }}>
        <TouchableOpacity
          onPress={onToggleAll}
          style={{
            width: 16,
            height: 16,
            borderWidth: 1,
            borderColor: allChecked ? colors.accent : colors.lineStrong,
            borderRadius: 3,
            backgroundColor: allChecked ? colors.accent : colors.card,
            alignItems: 'center',
            justifyContent: 'center',
          }}
          activeOpacity={0.75}
        >
          {allChecked && <Icon name="check" size={10} color={colors.white} />}
        </TouchableOpacity>
      </View>
      <View style={{ flex: 3, minWidth: 160 }}>
        <Text style={thStyle}>ITEM</Text>
      </View>
      <View style={{ flex: 1.2, minWidth: 80 }}>
        <Text style={thStyle}>TYPE</Text>
      </View>
      <View style={{ flex: 1.2, minWidth: 80 }}>
        <Text style={thStyle}>CATEGORY</Text>
      </View>
      <View style={{ flex: 1.2, minWidth: 90 }}>
        <Text style={thStyle}>STATE</Text>
      </View>
      <View style={{ width: 90, alignItems: 'flex-end', paddingRight: 4 }}>
        <Text style={thStyle}>PRICE</Text>
      </View>
      <View style={{ flex: 1.5, minWidth: 120, paddingLeft: 8 }}>
        <Text style={thStyle}>TAGS</Text>
      </View>
      <View style={{ flex: 1, minWidth: 80 }}>
        <Text style={thStyle}>UPDATED</Text>
      </View>
      <View style={{ width: 40 }} />
    </View>
  );
}

// ── ItemRow ──────────────────────────────────────────────────

interface ItemRowProps {
  item: Item;
  selected: boolean;
  onSelect: () => void;
  onPress: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onPreview: () => void;
  onDeactivate: () => void;
  locale: string;
  locationId: string;
}

function ItemRow({
  item: it,
  selected,
  onSelect,
  onPress,
  onEdit,
  onDuplicate,
  onPreview,
  onDeactivate,
  locale,
  locationId,
}: ItemRowProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;

  const t = SK.typeById(it.type);

  const priceFor = (): number | null => {
    const lp = it.locationPrices || {};
    if (locationId !== 'all' && lp[locationId] != null) return lp[locationId];
    return it.fields.base_price ?? null;
  };

  const rowStyle: ViewStyle = {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.line,
    backgroundColor: selected ? colors.accentSoft : colors.card,
    // row-attention: inset left 3px warn shadow
    ...(it.needs_attention
      ? {
          borderLeftWidth: 3,
          borderLeftColor: colors.warn,
        }
      : {
          borderLeftWidth: 0,
        }),
    ...Platform.select({
      web: { cursor: 'pointer' } as ViewStyle,
      default: {},
    }),
  };

  const mutedText: TextStyle = {
    fontSize: fontSize.sm,
    color: colors.ink3,
    fontFamily: theme.font.sans,
    includeFontPadding: false,
  };

  const fieldKeyStyle: TextStyle = {
    fontFamily: theme.font.mono,
    fontSize: fontSize.xs,
    color: colors.ink3,
    includeFontPadding: false,
  };

  const price = priceFor();

  return (
    <TouchableOpacity style={rowStyle} onPress={onPress} activeOpacity={0.75}>
      {/* Checkbox */}
      <View style={{ width: 36, alignItems: 'center' }}>
        <TouchableOpacity
          onPress={(e) => { e.stopPropagation?.(); onSelect(); }}
          style={{
            width: 16,
            height: 16,
            borderWidth: 1,
            borderColor: selected ? colors.accent : colors.lineStrong,
            borderRadius: 3,
            backgroundColor: selected ? colors.accent : colors.card,
            alignItems: 'center',
            justifyContent: 'center',
          }}
          activeOpacity={0.75}
        >
          {selected && <Icon name="check" size={10} color={colors.white} />}
        </TouchableOpacity>
      </View>

      {/* Item cell */}
      <View style={{ flex: 3, minWidth: 160, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <Thumb emoji={it.emoji} />
        <View style={{ minWidth: 0, flex: 1 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <Text
              style={{
                fontSize: fontSize.md,
                fontWeight: fontWeight.medium,
                color: colors.ink,
                fontFamily: theme.font.sans,
                includeFontPadding: false,
              }}
              numberOfLines={1}
            >
              {SK.itemName(it, locale)}
            </Text>
            {it.ai && it.ai.length > 0 && (
              <Icon name="sparkles" size={13} color={colors.accent} />
            )}
            {it.variants && it.variants.length > 0 && (
              <Chip tone="neutral">{it.variants.length} variants</Chip>
            )}
          </View>
          <Text style={fieldKeyStyle}>{it.ref}</Text>
        </View>
      </View>

      {/* Type */}
      <View style={{ flex: 1.2, minWidth: 80 }}>
        <Text style={mutedText} numberOfLines={1}>
          {t ? `${t.emoji} ${t.name}` : '—'}
        </Text>
      </View>

      {/* Category */}
      <View style={{ flex: 1.2, minWidth: 80 }}>
        <Text style={mutedText} numberOfLines={1}>
          {it.fields.category || '—'}
        </Text>
      </View>

      {/* State */}
      <View style={{ flex: 1.2, minWidth: 90 }}>
        <StateBadge state={it.state as any} />
        {it.state === 'SCHEDULED' && it.publish_at && (
          <Text style={[fieldKeyStyle, { marginTop: 3 }]}>{it.publish_at}</Text>
        )}
      </View>

      {/* Price */}
      <View style={{ width: 90, alignItems: 'flex-end', paddingRight: 4 }}>
        {price != null ? (
          <Text
            style={{
              fontFamily: theme.font.mono,
              fontSize: fontSize.sm,
              color: colors.ink,
              includeFontPadding: false,
            }}
          >
            {fmtINR(price)}
          </Text>
        ) : (
          <Text style={mutedText}>On enquiry</Text>
        )}
      </View>

      {/* Tags */}
      <View style={{ flex: 1.5, minWidth: 100, flexDirection: 'row', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
        {(it.tags || []).slice(0, 2).map(tg => (
          <Chip key={tg} tone="outline">{tg}</Chip>
        ))}
        {(it.tags || []).length > 2 && (
          <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans }}>
            +{it.tags.length - 2}
          </Text>
        )}
      </View>

      {/* Updated */}
      <View style={{ flex: 1, minWidth: 80 }}>
        <Text style={[mutedText, { ...Platform.select({ web: { whiteSpace: 'nowrap' } as TextStyle, default: {} }) }]} numberOfLines={1}>
          {it.updated}
        </Text>
      </View>

      {/* Actions */}
      <View style={{ width: 40, alignItems: 'center' }}>
        <Popover
          align="right"
          width={180}
          trigger={
            <Button kind="ghost" size="sm" iconOnly icon={<Icon name="more" size={14} color={colors.ink2} />} />
          }
        >
          {(close: () => void) => (
            <>
              <MenuItem icon="edit" onPress={() => { close(); onEdit(); }}>Edit</MenuItem>
              <MenuItem icon="dup" onPress={() => { close(); onDuplicate(); }}>Duplicate</MenuItem>
              <MenuItem icon="eye" onPress={() => { close(); onPreview(); }}>Preview</MenuItem>
              <MenuSeparator />
              <MenuItem icon="bolt-off" danger onPress={() => { close(); onDeactivate(); }}>Deactivate</MenuItem>
            </>
          )}
        </Popover>
      </View>
    </TouchableOpacity>
  );
}

// ── Main Screen ──────────────────────────────────────────────

export default function ItemsScreen() {
  const { theme, isDark } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;
  const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();
  const { location, locale } = useApp();
  const toast = useToast();
  // Plan gating: disable item creation when the org is at its item cap.
  const { isBlocked, refresh: refreshQuota } = useQuota();
  const itemsBlocked = isBlocked(METRIC_CATALOG_ITEMS);
  // On narrow screens the wide data table can't fit, so each row collapses
  // into a compact card (name + meta line + delete) and the column headers hide.
  const { isMobile, pad } = useResponsive();

  const [q, setQ] = useState('');
  const [type, setType] = useState('All');
  const [state, setState] = useState('All');
  const [cat, setCat] = useState('All');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [onlyAttention, setOnlyAttention] = useState(false);
  const [onlyAI, setOnlyAI] = useState(false);

  // ── Live data (sk-catalog-api) ──
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [types, setTypes] = useState<ApiItemType[]>([]);
  const [loading, setLoading] = useState(true);

  // ── Delete (soft = archive) confirmation ──
  const [delTarget, setDelTarget] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);

  const typeName = useCallback(
    (id?: string) => types.find(t => t.id === id)?.name ?? '—',
    [types],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, typesRes] = await Promise.all([getItems({ limit: 200 }), getItemTypes()]);
      const list = itemsRes.items ?? [];
      setTypes(typesRes.items ?? []);
      const enriched = list.map((it: any) => {
        const raw = it.fieldValues?.[0]?.valueJson;
        const name = raw != null ? (typeof raw === 'string' ? raw : String(raw)) : undefined;
        return { ...it, name };
      });
      setItems(enriched);
    } catch (err) {
      handleApiError(err, 'Could not load items');
    } finally {
      setLoading(false);
    }
  }, []);

  useReloadOnFocus(load);

  // Soft-delete (archive) the chosen item after confirmation, then refresh.
  const confirmDelete = useCallback(async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await deleteItem(delTarget.id);
      toast.success(`"${delTarget.name || delTarget.skuOrReference}" was removed from the catalog.`);
      setDelTarget(null);
      await load();
      // Archiving frees a unit of the item quota — refresh so gating re-opens.
      void refreshQuota();
    } catch (err) {
      handleApiError(err, 'Could not delete item');
    } finally {
      setDeleting(false);
    }
  }, [delTarget, load, toast]);

  const stateOpts = ['All', 'Published', 'Draft', 'Deactivated', 'Archived'];
  // Bundles are catalog items of the "Bundle" type; they're managed on the
  // Bundles screen and must not appear in the Items list.
  const bundleTypeId = useMemo(
    () => types.find(t => (t.name || '').toLowerCase() === 'bundle')?.id,
    [types],
  );
  const isBundle = useCallback(
    (it: any) => !!bundleTypeId && it.itemTypeId === bundleTypeId,
    [bundleTypeId],
  );
  const typeOpts = ['All', ...types.filter(t => (t.name || '').toLowerCase() !== 'bundle').map(t => t.name)];

  const stateMap: Record<string, string> = {
    Published: 'PUBLISHED',
    Draft: 'DRAFT',
    Deactivated: 'DEACTIVATED',
    Archived: 'ARCHIVED',
  };

  // Name search: the list response carries only the SKU/reference (not the
  // product title), so to let users search by NAME we also query the catalog's
  // search endpoint (which indexes titles) and match those item ids.
  const [nameMatchIds, setNameMatchIds] = useState<Set<string> | null>(null);
  useEffect(() => {
    const term = q.trim();
    if (!term) { setNameMatchIds(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res: any = await searchItems({ q: term, limit: 50 });
        const ids = (res.items ?? []).map((r: any) => r.id ?? r.catalogItemId ?? r.item?.id).filter(Boolean);
        if (!cancelled) setNameMatchIds(new Set(ids));
      } catch { if (!cancelled) setNameMatchIds(new Set()); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const rows = useMemo(() => {
    return items.filter(it => {
      // Bundles are not regular items — never list them here.
      if (isBundle(it)) return false;
      // Hide archived ("deleted") items unless explicitly viewing Archived.
      if (state !== 'Archived' && it.currentState === 'ARCHIVED') return false;
      if (type !== 'All' && typeName(it.itemTypeId) !== type) return false;
      if (state !== 'All' && it.currentState !== stateMap[state]) return false;
      if (q) {
        const ql = q.toLowerCase();
        const ref = (it.skuOrReference || '').toLowerCase();
        const id = (it.id || '').toLowerCase();
        const name = ((it as any).name || '').toLowerCase();
        const matchesRef = ref.includes(ql) || id.includes(ql) || name.includes(ql);
        const matchesName = nameMatchIds ? nameMatchIds.has(it.id) : false;
        if (!matchesRef && !matchesName) return false;
      }
      return true;
    });
  }, [items, q, type, state, typeName, nameMatchIds, isBundle]);

  const total = useMemo(() => items.filter(it => !isBundle(it)).length, [items, isBundle]);

  // Create a draft item of the chosen type, then refresh the list.
  const allChecked = rows.length > 0 && rows.every(r => sel.has(r.id));
  const toggleAll = useCallback(() => {
    setSel(allChecked ? new Set() : new Set(rows.map(r => r.id)));
  }, [allChecked, rows]);

  const toggle = useCallback((id: string) => {
    setSel(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }, []);

  // ── Publish (single endpoint applied per id) ──
  // "Publish all" targets every non-archived item — drafts get published and
  // already-published items get re-published (a new live version), matching the
  // editor's Re-publish behaviour.
  // CC-002 CR-7 — "publishable" now means the transition map says so, not
  // "anything that isn't archived". Same answer today for DRAFT/PUBLISHED/
  // DEACTIVATED, but it stops being a second, hand-maintained copy of the rule:
  // when the map changes, this follows.
  const publishableRows = useMemo(
    () => rows.filter(r => canTransition((r.currentState || 'DRAFT') as any, 'PUBLISHED')),
    [rows],
  );
  const [publishing, setPublishing] = useState(false);
  const [confirmPublishAll, setConfirmPublishAll] = useState(false);
  const [confirmPublishSel, setConfirmPublishSel] = useState(false);

  const runPublish = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return;
      setPublishing(true);
      try {
        const { ok, failed } = await publishItems(ids);
        if (ok.length) {
          toast.success(`Published ${ok.length} item${ok.length === 1 ? '' : 's'}.`);
        }
        if (failed.length) {
          // SKCATALOG-CC-002 CR-1/CR-7 — name the CAUSE, not just the count.
          // "Couldn't publish 3 items" leaves the operator with nowhere to go;
          // "3 need an HSN/SAC code" tells them exactly what to open the editor
          // for. Grouped by code so one toast covers the common case.
          const missingHsn = failed.filter(f => f.code === HSN_SAC_REQUIRED).length;
          const illegal = failed.filter(f => f.code === ILLEGAL_TRANSITION).length;
          const other = failed.length - missingHsn - illegal;
          const parts: string[] = [];
          if (missingHsn) {
            parts.push(
              `${missingHsn} need${missingHsn === 1 ? 's' : ''} an HSN/SAC code before publishing (required for GST)`,
            );
          }
          if (illegal) {
            parts.push(`${illegal} can't be published from their current state`);
          }
          if (other) parts.push(`${other} failed`);
          toast.error(
            `Couldn't publish ${failed.length} item${failed.length === 1 ? '' : 's'} — ${parts.join('; ')}.`,
          );
        }
        setSel(new Set());
        await load();
      } catch (err) {
        handleApiError(err, 'Could not publish items');
      } finally {
        setPublishing(false);
        setConfirmPublishAll(false);
        setConfirmPublishSel(false);
      }
    },
    [load, toast],
  );

  const publishSelected = useCallback(() => {
    if (sel.size === 0) return;
    setConfirmPublishSel(true);
  }, [sel.size]);
  const publishAll = useCallback(() => {
    if (publishableRows.length === 0) {
      toast.info('Everything is already published.');
      return;
    }
    setConfirmPublishAll(true);
  }, [publishableRows.length, toast]);

  const clearFilters = useCallback(() => {
    setType('All');
    setState('All');
    setCat('All');
    setOnlyAttention(false);
    setOnlyAI(false);
    setQ('');
  }, []);

  const activeFilters =
    [type, state, cat].filter(x => x !== 'All').length +
    (onlyAttention ? 1 : 0) +
    (onlyAI ? 1 : 0);

  // Styles
  const pageSubStyle: TextStyle = {
    fontSize: fontSize.md,
    color: colors.ink3,
    fontFamily: theme.font.sans,
    marginTop: 4,
    includeFontPadding: false,
  };

  const pageTitleStyle: TextStyle = {
    fontSize: 26,
    fontWeight: fontWeight.semibold,
    letterSpacing: -0.02 * 26,
    color: colors.ink,
    fontFamily: theme.font.sans,
    margin: 0,
    includeFontPadding: false,
  };

  const tblWrapStyle: ViewStyle = {
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.r3,
    overflow: 'hidden',
    ...Platform.select({
      web: { boxShadow: '0 1px 3px rgba(0,0,0,0.08)' } as ViewStyle,
      default: shadows.sm as ViewStyle,
    }),
  };

  const liveTh: TextStyle = {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.semibold,
    color: colors.ink3,
    textTransform: 'uppercase',
    letterSpacing: 0.04 * fontSize.xs,
    fontFamily: theme.font.sans,
    paddingVertical: 10,
  };

  const pillClearStyle: ViewStyle = {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 30,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.r2,
    backgroundColor: colors.card,
    ...Platform.select({
      web: { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' } as ViewStyle,
      default: {},
    }),
  };

  const locationCtx =
    location.id !== 'all' ? ` · prices shown for ${location.name}` : '';

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: colors.paper }}
      contentContainerStyle={{ padding: pad, paddingBottom: 64, maxWidth: CONTENT.wide, width: '100%' }}
    >
      {/* ── Page title row ── */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 20,
        }}
      >
        <View>
          <Text style={pageTitleStyle}>Items</Text>
          <Text style={pageSubStyle}>
            {rows.length} item{rows.length === 1 ? '' : 's'}{locationCtx}
          </Text>
        </View>

        {/* Right cluster: Import + New item */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <Button
            size="sm"
            icon={<Icon name="import" size={14} color={colors.ink} />}
            onPress={() => navigation.navigate('imports')}
          >
            Import
          </Button>

          <Popover
            align="right"
            width={220}
            trigger={
              <Button
                kind="accent"
                size="sm"
                hoverLift
                disabled={itemsBlocked}
                icon={<Icon name="plus" size={14} color={colors.paper} />}
                style={{
                  ...Platform.select({
                    web: { boxShadow: '0 4px 12px rgba(17,24,39,0.22)', transition: 'all 160ms ease' } as any,
                    default: {},
                  }),
                }}
                textStyle={{ fontWeight: '700', letterSpacing: 0.2 }}
              >
                New item
              </Button>
            }
          >
            {(close: () => void) => (
              <>
                <View
                  style={{
                    paddingVertical: 6,
                    paddingHorizontal: 10,
                    paddingBottom: 3,
                  }}
                >
                  <Text
                    style={{
                      fontSize: fontSize.xs,
                      color: colors.ink4,
                      textTransform: 'uppercase',
                      letterSpacing: 0.05 * fontSize.xs,
                      fontWeight: fontWeight.semibold,
                      fontFamily: theme.font.sans,
                      includeFontPadding: false,
                    }}
                  >
                    Choose a type
                  </Text>
                </View>
                {types.length === 0 && (
                  <View style={{ paddingVertical: 6, paddingHorizontal: 10 }}>
                    <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans }}>
                      No item types yet.
                    </Text>
                  </View>
                )}
                {types.map((t) => (
                  <MenuItem
                    key={t.id}
                    icon="box"
                    onPress={() => {
                      close();
                      // Open the editor in "new item" mode for this type so the
                      // user fills in fields first — instead of silently
                      // creating an empty draft in the list.
                      navigation.navigate('editor', { id: 'new', type: t.id });
                    }}
                  >
                    {t.name}
                  </MenuItem>
                ))}
                <MenuSeparator />
                <MenuItem
                  icon="sliders"
                  onPress={() => {
                    close();
                    navigation.navigate('types');
                  }}
                >
                  Create a new type…
                </MenuItem>
              </>
            )}
          </Popover>
        </View>
      </View>

      {/* ── Toolbar ── */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          marginBottom: 14,
        }}
      >
        {/* Search — your CommonSearch component */}
        <View style={{ width: 280, maxWidth: '100%' }}>
          <CommonSearch
            placeholder="Search by name or SKU…"
            value={q}
            onChange={setQ}
            isDark={isDark}
          />
        </View>

        <View style={{ width: 184 }}>
          <AppDropdown
            compact
            isDark={isDark}
            placeholder="Type"
            value={type}
            items={typeOpts.map((o) => ({ label: o === 'All' ? 'Type' : o, value: o }))}
            onSelect={setType}
          />
        </View>
        <View style={{ width: 184 }}>
          <AppDropdown
            compact
            isDark={isDark}
            placeholder="State"
            value={state}
            items={stateOpts.map((o) => ({ label: o === 'All' ? 'State' : o, value: o }))}
            onSelect={setState}
          />
        </View>
        {activeFilters > 0 && (
          <TouchableOpacity style={pillClearStyle} onPress={clearFilters} activeOpacity={0.75}>
            <Icon name="x" size={12} color={colors.ink2} />
            <Text
              style={{
                fontSize: fontSize.sm,
                fontWeight: fontWeight.medium,
                color: colors.ink2,
                fontFamily: theme.font.sans,
                includeFontPadding: false,
              }}
            >
              Clear
            </Text>
          </TouchableOpacity>
        )}

        {/* Push the publish actions to the right of the toolbar row. */}
        <View style={{ flex: 1, minWidth: 0 }} />

        {/* Publish — only the selected items (needs a selection).
            Colors come from the adaptive Button (enabled → primary, disabled → muted). */}
        <Button
          kind="accent"
          size="sm"
          icon="check-circle"
          onPress={publishSelected}
          disabled={publishing || sel.size === 0 || allChecked}
        >
          {`Publish${sel.size > 0 && !allChecked ? ` (${sel.size})` : ''}`}
        </Button>

        {/* Publish all — every unpublished item. */}
        <Button
          kind="accent"
          size="sm"
          hoverLift
          icon="check-circle"
          onPress={publishAll}
          disabled={publishing}
          textStyle={{ fontWeight: '700', letterSpacing: 0.2 }}
        >
          {publishing ? 'Publishing…' : `Publish all${allChecked ? ` (${sel.size})` : ''}`}
        </Button>
      </View>


      {/* ── Data table (live — sk-catalog-api /items) ── */}
      <View style={tblWrapStyle}>
        {/* Header — desktop only; on mobile each row is a self-describing card. */}
        {!isMobile && (
          <View style={{ flexDirection: 'row', paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: colors.line, backgroundColor: colors.paper2 }}>
            <View style={{ width: 40, alignItems: 'center', justifyContent: 'center' }}>
              <TouchableOpacity onPress={toggleAll} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} style={Platform.OS === 'web' ? { cursor: 'pointer' } as any : undefined}>
                <View style={{ width: 18, height: 18, borderRadius: 5, borderWidth: allChecked ? 0 : 1.5, borderColor: colors.lineStrong, backgroundColor: allChecked ? colors.accent : colors.card, alignItems: 'center', justifyContent: 'center' }}>
                  {allChecked && <Icon name="check" size={10} color={colors.white} />}
                </View>
              </TouchableOpacity>
            </View>
            <View style={{ flex: 2, minWidth: 200 }}><Text style={liveTh}>Item</Text></View>
            <View style={{ flex: 1, minWidth: 140 }}><Text style={liveTh}>Type</Text></View>
            <View style={{ minWidth: 120 }}><Text style={liveTh}>State</Text></View>
            <View style={{ flex: 1, minWidth: 140 }}><Text style={liveTh}>Updated</Text></View>
            <View style={{ width: 96, alignItems: 'center' }}><Text style={liveTh}>Delete</Text></View>
          </View>
        )}

        {loading ? (
          <View style={{ padding: 36, alignItems: 'center' }}>
            <ActivityIndicator color={colors.accent} />
          </View>
        ) : rows.length === 0 ? (
          <View style={{ padding: 24 }}>
            <Empty
              icon={<Icon name="box" size={28} color={colors.ink4} />}
              title={total === 0 ? 'No items yet' : 'No items match your filters'}
              action={
                total === 0 ? undefined : (
                  <Button kind="accent" icon={<Icon name="x" size={14} color={colors.paper} />} onPress={clearFilters}>
                    Clear filters
                  </Button>
                )
              }
            >
              {total === 0 ? 'Use “New item” to create your first draft.' : 'Try adjusting your filters.'}
            </Empty>
          </View>
        ) : (
          rows.map((it) => (
            <TouchableOpacity
              key={it.id}
              activeOpacity={0.7}
              onPress={() => navigation.navigate('editor', { id: it.id })}
              style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 12, paddingHorizontal: 12, borderBottomWidth: 1, borderBottomColor: colors.line }}
            >
              {/* Select checkbox — stops row navigation when tapped. */}
              <View
                style={{ width: 40, alignItems: 'center', justifyContent: 'center' }}
                onStartShouldSetResponder={() => true}
              >
                <TouchableOpacity
                  onPress={() => toggle(it.id)}
                  accessibilityLabel={`Select ${(it as any).name || it.skuOrReference || 'item'}`}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  style={Platform.OS === 'web' ? { cursor: 'pointer' } as any : undefined}
                >
                  <View style={{ width: 18, height: 18, borderRadius: 5, borderWidth: sel.has(it.id) ? 0 : 1.5, borderColor: colors.lineStrong, backgroundColor: sel.has(it.id) ? colors.accent : colors.card, alignItems: 'center', justifyContent: 'center' }}>
                    {sel.has(it.id) && <Icon name="check" size={10} color={colors.white} />}
                  </View>
                </TouchableOpacity>
              </View>
              <View style={{ flex: isMobile ? 1 : 2, minWidth: isMobile ? 0 : 200, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                <View style={{ width: 32, height: 32, borderRadius: radii.r2, backgroundColor: colors.paper3, borderWidth: 1, borderColor: colors.line, alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="box" size={15} color={colors.ink4} />
                </View>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text numberOfLines={1} style={{ fontFamily: theme.font.sans, fontSize: fontSize.md, fontWeight: fontWeight.medium, color: colors.ink }}>
                    {(it as any).name || it.skuOrReference || it.id.slice(0, 8)}
                  </Text>
                  {((it as any).name && it.skuOrReference) ? (
                    <Text numberOfLines={1} style={{ fontFamily: theme.font.mono, fontSize: fontSize.xs, color: colors.ink4 }}>
                      {it.skuOrReference}
                    </Text>
                  ) : null}
                  {/* Mobile meta line — folds the hidden Type/State columns in. */}
                  {isMobile && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 5 }}>
                      <StateBadge state={(it.currentState || 'DRAFT') as any} />
                      <Text numberOfLines={1} style={{ flex: 1, fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans }}>
                        {typeName(it.itemTypeId)}
                      </Text>
                    </View>
                  )}
                </View>
              </View>
              {!isMobile && (
                <View style={{ flex: 1, minWidth: 140 }}>
                  <Text style={{ fontSize: fontSize.md, color: colors.ink2, fontFamily: theme.font.sans }}>
                    {typeName(it.itemTypeId)}
                  </Text>
                </View>
              )}
              {!isMobile && (
                <View style={{ minWidth: 120 }}>
                  <StateBadge state={(it.currentState || 'DRAFT') as any} />
                </View>
              )}
              {!isMobile && (
                <View style={{ flex: 1, minWidth: 140 }}>
                  <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans }}>
                    {it.updatedAt ? new Date(it.updatedAt).toLocaleDateString() : '—'}
                  </Text>
                </View>
              )}
              {/* Delete (soft = archive) — red bin, opens the confirm dialog. */}
              <View
                style={{ width: 96, alignItems: 'center', justifyContent: 'center' }}
                onStartShouldSetResponder={() => true}
              >
                <Button
                  kind="danger"
                  size="xs"
                  iconOnly
                  icon="trash"
                  aria-label="Delete item"
                  style={{ alignSelf: 'center' }}
                  onPress={() => setDelTarget(it)}
                />
              </View>
            </TouchableOpacity>
          ))
        )}
      </View>

      {/* Delete confirmation (your ConfirmDialog component) */}
      <ConfirmDialog
        visible={!!delTarget}
        title="Delete item?"
        message={
          delTarget
            ? `"${(delTarget as any).name || delTarget.skuOrReference}" will be removed from your catalog. This archives the item (kept for history); it won't appear in the catalog or storefront.`
            : ''
        }
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setDelTarget(null)}
        onClose={() => setDelTarget(null)}
      />

      {/* Publish-all confirmation — success-style dialog */}
      <SuccessDialog
        visible={confirmPublishAll}
        tone="success"
        title="Publish all items?"
        message={`${publishableRows.length} item${publishableRows.length === 1 ? '' : 's'} will be published (drafts go live, already-published items get a fresh version). You can deactivate any of them later.`}
        primaryAction={{ label: publishing ? 'Publishing…' : 'Publish all', onPress: () => runPublish(publishableRows.map(r => r.id)) }}
        secondaryAction={{ label: 'Cancel', onPress: () => setConfirmPublishAll(false) }}
        onClose={() => setConfirmPublishAll(false)}
      />

      {/* Publish-selected confirmation — success-style dialog */}
      <SuccessDialog
        visible={confirmPublishSel}
        tone="success"
        title={`Publish ${sel.size} item${sel.size === 1 ? '' : 's'}?`}
        message={`The selected item${sel.size === 1 ? '' : 's'} will be published (drafts go live, already-published items get a fresh version). You can deactivate any of them later.`}
        primaryAction={{ label: publishing ? 'Publishing…' : 'Publish', onPress: () => runPublish([...sel]) }}
        secondaryAction={{ label: 'Cancel', onPress: () => setConfirmPublishSel(false) }}
        onClose={() => setConfirmPublishSel(false)}
      />
    </ScrollView>
  );
}
